<template>
    <NewFutureDeliveryRequest
        v-if="loaded"
        :show="show"
        :customer="customer"
        :designer="designer"
        :material-types="materialTypes"
        :material-size-types="materialSizeTypes"
        :has-past-due-invoices="!!numberPastDueInvoices"
        :days-past-due="daysPastDue"
        @update:show="$emit('update:show', $event)"
        @addFutureDeliveryRequest="addNewFutureDeliveryRequest"
        @cancel="$emit('cancel')"
        @showPastDueInvoicesMessage="showPastDueInvoicesMessage"
    />
</template>
<script>
import SmartyStreetsSDK from "smartystreets-javascript-sdk";
import { mapActions } from "vuex";
import swal from "sweetalert2";
import NewFutureDeliveryRequest from "./_components/NewFutureDeliveryRequest.vue";
import { store } from "src/global-store.js";

const SmartyStreetsCore = SmartyStreetsSDK.core;
const { Lookup } = SmartyStreetsSDK.usStreet;

const credentials = new SmartyStreetsCore.SharedCredentials(
    process.env.VUE_APP_SMARTY_WEBSITE_KEY,
);
const usStreetClientBuilder = new SmartyStreetsCore.ClientBuilder(credentials);
const streetClient = usStreetClientBuilder.buildUsStreetApiClient();

export default {
    name: "NewDeliveryRequestModule",
    components: { NewFutureDeliveryRequest },
    props: {
        show: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            materialTypes: [],
            materialSizeTypes: ["Boxes", "Cubes", "Each", "Pallets"],
            pastDueInvoiceMessage: "",
            numberPastDueInvoices: 0,
            daysPastDue: 0,
            loaded: false,
        };
    },
    computed: {
        me() {
            return store.me;
        },
        customer() {
            return {
                id: this.me.customer_id,
                name: this.me.customer_name,
            };
        },
        designer() {
            return {
                id: this.me.designer_id,
                name: this.me.designer_name,
            };
        },
    },
    async created() {
        this.materialTypes = await this.fetchMaterialTypes();
        const info = await this.getPastDueInvoiceInfo();
        this.pastDueInvoiceMessage = info.message;
        this.numberPastDueInvoices = info.count;
        this.daysPastDue = info.daysPastDue || 0;
        this.loaded = true;
    },
    methods: {
        ...mapActions("$_futureDeliveryRequest", [
            "getPastDueInvoiceInfo",
            "addFutureDeliveryRequest",
            "fetchMaterialTypes",
        ]),
        async addNewFutureDeliveryRequest(deliveryRequest) {
            let sendRequest = false;
            if (process.env.NODE_ENV !== "production") {
                // bypass address verification
                sendRequest = true;
            } else {
                const lookup = new Lookup();
                let addressLookup;
                lookup.street = deliveryRequest.deliveryAddress;
                lookup.city = deliveryRequest.deliveryCity;
                lookup.zipCode = deliveryRequest.deliveryZip;
                lookup.state = "CO";
                lookup.match = "strict";
                const response = await streetClient.send(lookup);
                addressLookup = response.lookups[0];
                const { result } = addressLookup;

                // there was a match
                if (result.length) {
                    const candidate = result[0];
                    if (
                        addressLookup.street !== candidate.deliveryLine1 ||
                        addressLookup.city !== candidate.components.cityName ||
                        addressLookup.zipCode !== candidate.components.zipCode
                    ) {
                        // entered address is not the same as the candidate
                        const { value } = await swal({
                            title: "Delivery Address Verification",
                            allowEscapeKey: false,
                            allowOutsideClick: false,
                            showConfirmButton: true,
                            confirmButtonText: "Use verified address",
                            confirmButtonColor: "#3085d6",
                            showCancelButton: true,
                            cancelButtonText: "Use my original address",
                            cancelButtonColor: "#ff8d72",
                            type: "warning",
                            html: `
              <div class="row mb-3">
                <div class="col-12"><h3 class="mb-0">Original Address:</h3></div>
                <div class="col-12">${addressLookup.street}</div>
                <div class="col-12">${addressLookup.city}, ${addressLookup.state} ${addressLookup.zipCode}</div>
              </div>
              <div class="row">
                <div class="col-12"><h3 class="mb-0">Verified Address:</h3></div>
                <div class="col-12">${candidate.deliveryLine1}</div>
                <div class="col-12">${candidate.lastLine}</div>
              </div>
              `,
                        });
                        if (value) {
                            deliveryRequest.deliveryAddress =
                                candidate.deliveryLine1;
                            deliveryRequest.deliveryCity =
                                candidate.components.cityName;
                            deliveryRequest.deliveryZip =
                                candidate.components.zipCode;
                            deliveryRequest.deliveryAddressVerified = true;
                            sendRequest = true;
                        } else {
                            deliveryRequest.deliveryAddressVerified = false;
                            sendRequest = true;
                        }
                    } else {
                        // perfect match
                        deliveryRequest.deliveryAddressVerified = true;
                        sendRequest = true;
                    }
                } else {
                    const { value } = await swal({
                        title: "Delivery Address Not Verified",
                        allowEscapeKey: false,
                        allowOutsideClick: false,
                        showConfirmButton: true,
                        confirmButtonText: "Use this address anyway",
                        confirmButtonColor: "#ec250d",
                        showCancelButton: true,
                        cancelButtonText: "Let me change the address",
                        cancelButtonColor: "#3085d6",
                        type: "error",
                        html: `
            <div class="col-12">${addressLookup.street}</div>
            <div class="col-12">${addressLookup.city}, ${addressLookup.state} ${addressLookup.zipCode}</div>
            `,
                    });
                    if (value) {
                        deliveryRequest.deliveryAddressVerified = false;
                        sendRequest = true;
                    }
                }
            }
            if (sendRequest) {
                await this.addFutureDeliveryRequest(deliveryRequest);
                this.$emit("update:show", false);
                swal({
                    title: "Success!",
                    text: "Your future delivery request has been received and a confirmation email was sent.",
                    buttonsStyling: false,
                    confirmButtonClass: "btn btn-success btn-fill",
                    type: "success",
                });
            }
        },
        showPastDueInvoicesMessage() {
            const invoiceLabel =
                this.numberPastDueInvoices > 1 ? "Invoices" : "Invoice";
            swal({
                title: `${this.numberPastDueInvoices} Past Due ${invoiceLabel}`,
                text: this.pastDueInvoiceMessage,
                buttonsStyling: false,
                confirmButtonClass: "btn btn-danger btn-fill",
                type: "error",
            });
        },
    },
};
</script>
